import { addMinutes, subMinutes, addDays, addMonths, setDate, setHours, isAfter, isBefore } from 'date-fns'
import { intersection, cloneDeep } from 'lodash'
import { formatDate, toHourClock, today } from '@/utils/date'
import { numberFormat } from '@/utils/number'
import { toEllipsis } from '@/utils/string'
import { syncFromOtherCompany, syncFromWalkin, syncFromResraku } from '@/resources/reservation/syncTypeMap'
import { isResrakuSource } from '@/resources/reservation/syncSourceMap'
import { isWalkin, isResraku, isBlock } from '@/resources/reservation/typeMap'
import typeRoomMap from '@/resources/reservation/typeRoomMap'
import typeSmokingMap from '@/resources/reservation/typeSmokingMap'
import typeTableMap from '@/resources/reservation/typeTableMap'

/**
 * 予約のテーブルが重複しているかを返します
 * @param {object} reserveA 予約A
 * @param {object} reserveB 予約B
 * @param {boolean} includeSame 同じ予約を含める
 * @returns {boolean} 重複しているか
 */
export function isTableOverlap(reserveA, reserveB, includeSame) {
  return (
    (includeSame || reserveA.id !== reserveB.id) &&
    ![...reserveA.tableIds, ...reserveB.tableIds].includes(-1) &&
    !!intersection(reserveA.tableIds, reserveB.tableIds).length
  )
}

/**
 * 予約の時間が重複しているかを返します
 * @param {object} reserveA 予約A
 * @param {object} reserveB 予約B
 * @param {boolean} includeSame 同じ予約を含める
 * @returns {boolean} 重複しているか
 */
export function isTimeOverlap(reserveA, reserveB, includeSame) {
  if (!includeSame && reserveA.id === reserveB.id) return false
  const startA = reserveA.reserveDatetime
  const startB = reserveB.reserveDatetime
  const endA = addMinutes(startA, reserveA.utilizationTime + reserveA.resetTime)
  const endB = addMinutes(startB, reserveB.utilizationTime + reserveB.resetTime)
  return startB < endA && startA < endB
}

/**
 * 予約のテーブルと時間が重複しているかを返します
 * @param {object} reserveA 予約A
 * @param {object} reserveB 予約B
 * @param {boolean} includeSame 同じ予約を含める
 * @returns {boolean} 重複しているか
 */
export function isOverlap(reserveA, reserveB, includeSame) {
  return isTableOverlap(reserveA, reserveB, includeSame) && isTimeOverlap(reserveA, reserveB, includeSame)
}

/** 予約者名 */
export function reserveName({ reserveName: _reserveName, customer, type, syncType, syncSource }, isCustomer = false) {
  if (isBlock(type)) return 'ブロック'
  if (_reserveName && !isCustomer) return _reserveName
  const { lastName, lastNameKana: lastKana, firstName, firstNameKana: firstKana, tel, tel1, tel2 } = customer
  const prefix =
    syncFromOtherCompany[syncType] ||
    syncFromResraku[syncType] ||
    ([4, 5, 6].includes(syncType) && (isResrakuSource[syncSource] || isResraku(type)) && 'ぐるなび') ||
    ''
  const name = lastName && firstName ? `${lastName} ${firstName}` : lastName || firstName || ''
  const kana = lastKana && firstKana ? `${lastKana} ${firstKana}` : lastKana || firstKana || ''
  const rName = name || kana || tel || tel1 || tel2
  return prefix && rName
    ? `${prefix}_${rName}`
    : prefix || rName || ((isWalkin(type) || syncFromWalkin(syncType)) && 'ウォークイン') || ''
}

/** 予約人数 */
export function reserveNumDisplay(reserve, short = false) {
  const { adults, children } = reserve
  if (!adults && !children) return ''
  const adultsLabel = short ? '大' : '大人：'
  const childrenLabel = short ? '子' : '子供：'
  return adults && children ? `${adultsLabel}${adults}名 ${childrenLabel}${children}名` : `${adults || children}名`
}

/**
 * 選択された席と一致していない席指定条件を返します
 * @param {object} reservation 予約
 * @param {object} tables 全てのテーブル(ステートから)
 * @param {object} syncGnavis 全てのぐるなび予約連携関連情報（ステートから）
 * @returns {Array} 一致していない席指定条件（日本語名）
 */
export function unMatchedTableTypes(reservation, { byId: tables }, { byId: syncGnavis }) {
  if (!tables || !syncGnavis) return []
  const { tableIds, tableType, tableTypeLabel, privRoomType, smokingType } = reservation
  if (!tableIds.length || tableIds.includes(-1) || (!tableType && !privRoomType && !smokingType)) return []
  const unMatched = tableIds.reduce(
    (matched, id) => {
      const table = tables[id] || {}
      const syncGnavi = syncGnavis[table.syncGnaviId]
      if (!syncGnavi) {
        return {
          ...matched,
          smokingType:
            matched.smokingType ||
            (table.smokingType && table.smokingType !== smokingType && [1, 2].includes(smokingType)),
        }
      }
      return {
        tableType: matched.tableType || (!!tableType && syncGnavi.tableType !== tableType),
        privRoomType:
          matched.privRoomType ||
          (!!privRoomType && !!syncGnavi.privRoomType && syncGnavi.privRoomType !== privRoomType),
        smokingType:
          matched.smokingType ||
          (!!syncGnavi.smokingType && [1, 2].includes(smokingType) && syncGnavi.smokingType !== smokingType),
      }
    },
    { tableType: false, privRoomType: false, smokingType: false },
  )
  return [
    unMatched.tableType && (tableTypeLabel || typeTableMap[tableType]),
    unMatched.smokingType && typeSmokingMap[smokingType],
    unMatched.privRoomType && typeRoomMap[privRoomType],
  ].filter(o => o)
}

/** 適用されているクーポン、ポイントを表示 */
export function couponPoint(reserve) {
  const point = reserve.settlementPoint || 0
  const coupon = reserve.coupon?.discount || 0
  const prefix = reserve.settlement === 7 ? ' ポイント・クーポン利用' : ''

  return point || coupon ? `${prefix}${numberFormat(coupon + point)}円分` : ''
}

/** 適用されているクーポン、ポイントの内訳を表示 */
export function couponPointNote(reserve) {
  // レスラク連携予約の時は非表示
  if (isResrakuSource[reserve.syncSource]) return ''

  const point = reserve.settlementPoint || 0
  const coupon = reserve.coupon?.discount || 0

  if (coupon && point) {
    return `（ポイント${numberFormat(point)}pt + クーポン${numberFormat(coupon)}円）`
  } else if (coupon) {
    return `（クーポン${numberFormat(coupon)}円）`
  } else if (point) {
    return `（ポイント${numberFormat(point)}pt）`
  }
  return ''
}

/**
 * レスラク連携予約の分類を返します
 * @param {object} 予約
 * @returns {object}
 */
export function isResrakuReserve({ syncType, syncSource, status, rsvType }) {
  return {
    // レスラク経由のぐるなびネット、ホットペッパー、 Retty
    isResraku: isResrakuSource[syncSource] && [4, 10, 16].includes(syncType),
    // レスラク経由の食べログ、ozmall予約
    isResrakuOzmollTabelog: isResrakuSource[syncSource] && [9, 14].includes(syncType),
    // レスラク経由のぐるなびネット予約（リクエスト）
    request: isResrakuSource[syncSource] && syncType === 4 && status === 2 && rsvType === 2,
    // レスラク経由のぐるなびネット予約（問い合わせ）
    inquiry: isResrakuSource[syncSource] && syncType === 4 && status === 2 && rsvType === 3,
    // レスラク経由のホットペッパー予約（リクエスト）
    hotpepper: isResrakuSource[syncSource] && syncType === 10 && status === 2 && rsvType === 2,
    // レスラク経由のRetty予約（リクエスト）
    retty: isResrakuSource[syncSource] && syncType === 16 && status === 2 && rsvType === 2,
  }
}

/**
 * レスラク連携確定済み予約の分類を返します
 * @param {object} 予約
 * @returns {object}
 */
export function isConfirmedResrakuReserve({ syncType, syncSource, status, reserveDatetime }) {
  const reserveDate = toHourClock(reserveDatetime).date
  const isConfirmedResraku =
    isResrakuSource[syncSource] && [4, 5, 6, 7, 8, 9, 10].includes(status) && [4, 9, 10, 14, 16].includes(syncType)
  const verifyPeriod = limit => {
    // 対象外: 0
    if (!isConfirmedResraku) return 0
    // 来店日前: 2（来店日翌日の6:00まで）
    if (today() <= reserveDate) return 2
    // 来店日後 ~ 対象期間内: 3
    if (reserveDate < today() && limit) return 3
    // 対象期間外: 1
    return 1
  }
  const currentDate = formatDate(new Date())
  const targetDate = amount => formatDate(addDays(reserveDate, amount))
  // 来店日+7日5:59
  const gnavi = syncType === 4 ? verifyPeriod(today() < targetDate(7)) : 0
  // 来店日+6日23:59 or 来店翌月2日23:59
  const tabelog =
    syncType === 9
      ? verifyPeriod(currentDate <= targetDate(6) && currentDate <= formatDate(setDate(addMonths(reserveDate, 1), 2)))
      : 0
  // 来店日+5日23:59
  const hotpepper = syncType === 10 ? verifyPeriod(currentDate <= targetDate(5)) : 0
  // 来店日+10日23:59
  const ozmall = syncType === 14 ? verifyPeriod(currentDate <= targetDate(10)) : 0
  // 来店日+5日23:59 or 来店翌月1日23:59
  const retty =
    syncType === 16
      ? verifyPeriod(currentDate <= targetDate(5) && currentDate <= formatDate(setDate(addMonths(reserveDate, 1), 1)))
      : 0
  // 確定済みのレスラク予約
  const confirmed = isConfirmedResraku && (gnavi > 1 || tabelog > 1 || hotpepper > 1 || ozmall > 1 || retty > 1)
  // 確定済みのレスラク予約（操作対象期間外）
  const isOver = isConfirmedResraku && (gnavi === 1 || tabelog === 1 || hotpepper === 1 || ozmall === 1 || retty === 1)
  return { confirmed, gnavi, tabelog, hotpepper, ozmall, retty, isOver }
}

/**
 * 決済情報を表示するかどうかを返します
 * @param {object} 予約
 * @returns {Boolean}
 */
export function displaySettlementInfo(reserve) {
  const { syncSource, syncType, settlement, settlementPoint, coupon } = reserve
  // レスラク経由のHP予約でお食事券利用
  const isResrakuHP = syncSource === 5 && syncType === 10 && settlement === 3
  return !!settlementPoint || !!coupon?.discount || isResrakuHP
}

/**
 * footerのアラートを返します
 * @param {Array} アラートの中身[その他、タバコ、席タイプ]
 * @returns {string} 整形された文字列
 */
export function reserveFooterAlert(alerts, max = 99) {
  const _alerts = cloneDeep(alerts).filter(o => o)
  if (!_alerts.length) return ''
  const allCount = _alerts.join('・').length
  if (allCount > max) {
    _alerts[0] = `${toEllipsis(_alerts[0], _alerts[0].length - (allCount - max))}`
  }
  return _alerts.join('・')
}

/**
 * 来店実績確定後を判定します
 * @param {Date} reserveDatetime 予約日時
 * @param {Date} datetime 基準日時
 * @returns {boolean} 予約日時の7日後AM6:00が基準日時を過ぎているかの真偽値
 */
export function isAfterVisitConfirmation(reserveDatetime, datetime = new Date()) {
  // 来店実績は予約日時の7日後AM6:00に確定する
  const achievementSettleDateTime = setHours(addDays(toHourClock(reserveDatetime).date, 7), 6)
  return datetime.getTime() >= achievementSettleDateTime.getTime()
}

/**
 * 現在日時が、設定された予約時間枠+リセットタイム ~ 予約時間の
 * 範囲内であるような予約を抽出して返却します
 * @param {Array} reservations 予約一覧
 * @param {date} now 現在日時
 * @param {number} utilizationTime 予約時間枠(ランチまたはディナー)
 * @param {number} resetTime リセットタイム
 * @param {Array} 抽出後の予約一覧
 */
export function imminentReservations(reservations, now, utilizationTime, resetTime) {
  return reservations.filter(
    r =>
      isAfter(now, subMinutes(r.reserveDatetime, utilizationTime + resetTime)) &&
      isBefore(now, addMinutes(r.reserveDatetime, r.utilizationTime)),
  )
}
